/**
 * @file Select component
 *
 * It convert a default select into a functional CSS editable select
 */

import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import BaseComponent from '../BaseComponent';
import { Key } from '../../utils/constants';
import Manipulator from '../../utils/dom/manipulator';
import { ClassNames } from '../../utils/dom/css-classes';

const NAME = 'Select';

const DATA_KEY = 'lta.select';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';
const CLEAR = '.clear';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
const EVENT_CLICK = `click${EVENT_KEY}`;
const EVENT_MOUSEDOWN = `mousedown${EVENT_KEY}`;
const EVENT_BLUR = `blur${EVENT_KEY}`;
const EVENT_CLEAR = `click${EVENT_KEY}${CLEAR}`;
const EVENT_CLEAR_KEYDOWN = `keydown${EVENT_KEY}${CLEAR}`;

const SELECTOR_DATA_TOGGLE = '[data-lta-select]';

const CLASSNAME_OPTION_ACTIVE = 'lta-select__item--active';
const CLASSNAME_OPTION_SELECTED = 'lta-select__item--selected';

const OptionListNavigationDirection = Object.freeze({
	Up: 'up',
	Down: 'down'
});

class Select extends BaseComponent {
	constructor(element) {
		super(element);
		this.onchangeEvent = document.createEvent('HTMLEvents');
		this.onchangeEvent.initEvent('change', true, true);

		this._selectWrapper = element;
		this._selectNativeElement;
		this._selectedElement;
		this._itemsWrapper;
		this._placeholderText = '- Choose -';

		// this._items will not have the placeholder included. Placeholder index in native select is 0
		// since placeholder is actually an option with disabled, selected and hidden attributes
		this._items = [];

		this.state = {
			opened: false,
			selectedIndex: undefined,
			selectedItem: undefined,
			isMultiSelect: false,
			selectedOptions: [] // For multiselect only.
		};

		this.state.isMultiSelect = Manipulator.getDataAttribute(this._element, 'isMultiselect');

		if (this._selectWrapper) {
			this.init();
		}
	}

	static get NAME() {
		return NAME;
	}

	get selectedOptions() {
		return this.state.selectedOptions;
	}

	set selectedOptions(selectedOptions) {
		this.state.selectedOptions = selectedOptions;
		this.renderMultiSelect();
	}

	init() {
		this._selectNativeElement = SelectorEngine.findOne('select', this._selectWrapper);
		this._itemsWrapper = SelectorEngine.findOne('.lta-select__items-wrapper', this._selectWrapper);
		this._selectedElement = SelectorEngine.findOne('.lta-select__selected', this._selectWrapper);
		this._clearButton = SelectorEngine.findOne('.lta-select__clear', this._selectWrapper);
		this._dropdownCaret = SelectorEngine.findOne('.lta-select__caret-down', this._selectWrapper);

		for (const option of this._selectNativeElement.options) {
			option.dataset.ssId = option.value;
		}

		if (this.state.isMultiSelect) {
			this.initMultiSelect();
		} else {
			this.initSelect();
		}

		this.setEventListeners();
	}

	initSelect() {
		this._selectNativeElement.selectedIndex = this._selectNativeElement.selectedIndex < 0 ? 0 : this._selectNativeElement.selectedIndex;
		// first children is the span element
		this._selectedElement.children[0].textContent =
			this._selectNativeElement?.options[this._selectNativeElement.selectedIndex].textContent;

		// adds placeholder if it has one
		const index = this._selectNativeElement.selectedIndex;
		if (this._selectNativeElement?.options[index].disabled && this._selectNativeElement?.options[index].value == '') {
			this._selectedElement.classList.add('lta-select__selected--placeholder');
		}
		this._placeholderText = this._selectNativeElement?.options[this._selectNativeElement.selectedIndex]?.textContent;

		this.generateItems();
	}

	initMultiSelect() {
		const selectedIndex = this._selectNativeElement.selectedIndex < 0 ? 0 : this._selectNativeElement.selectedIndex;

		if (selectedIndex >= 0) {
			// first children is the span element
			this._selectedElement.children[0].textContent = this._selectNativeElement?.options[selectedIndex].textContent;
			this._placeholderText = this._selectNativeElement?.options[selectedIndex]?.textContent;

			// adds placeholder if it has one
			if (
				this._selectNativeElement?.options[selectedIndex].disabled &&
				this._selectNativeElement?.options[selectedIndex].value == ''
			) {
				this._selectedElement.classList.add('lta-select__selected--placeholder');
			}
		}

		this.generateItems();

		this._selectedElements = SelectorEngine.find(':not([disabled])[selected]', this._selectNativeElement);
		const options = this._itemsWrapper.querySelectorAll('.lta-select__item');
		const selectedValues = new Set(this._selectedElements.map((item) => item.value+""));
		for (const option of options) {
			const value = Manipulator.getDataAttribute(option, 'ssId');
			if (selectedValues.has(value+"")) {
				option.classList.add(CLASSNAME_OPTION_SELECTED);
			}
		}
		this.selectedOptions = SelectorEngine.find(`.${CLASSNAME_OPTION_SELECTED}`, this._itemsWrapper);
	}

	activeEventOption(event) {
		return event.target.getAttribute('role') === 'option'
			? event.target
			: SelectorEngine.findOne(`.${CLASSNAME_OPTION_ACTIVE}`, this._itemsWrapper);
	}

	renderLabel() {
		if (!this.state.isMultiSelect) {
			// first children is the span element
			this._selectedElement.children[0].textContent =
				this._selectNativeElement?.options[this._selectNativeElement.selectedIndex].textContent;

			// adds placeholder if it has one
			const index = this._selectNativeElement.selectedIndex;
			if (this._selectNativeElement?.options[index].disabled && this._selectNativeElement?.options[index].value == '') {
				this._selectedElement.classList.add('lta-select__selected--placeholder');
			}
		} else {
			const optionValues = this.selectedOptions.map((option) => '' + Manipulator.getDataAttribute(option, 'ssId'));
			const optionLabels = this.selectedOptions.map((option) => option.textContent);
			for (const option of this._selectNativeElement?.options) {
				option.selected = optionValues.includes(option.value);
			}
			if (optionValues.length > 0) {
				this._selectedElement.children[0].textContent = optionLabels.join(', ');
				this._selectedElement.classList.remove('lta-select__selected--placeholder');
			} else {
				this._selectedElement.children[0].textContent = this._placeholderText;
			}
		}
	}

	// this is used in AddressSearch/index.js
	reBindSelectItems() {
		// first children is the span element
		this._selectedElement.children[0].textContent =
			this._selectNativeElement?.options[this._selectNativeElement.selectedIndex].textContent;

		// adds placeholder if it has one
		const index = this._selectNativeElement.selectedIndex;
		if (this._selectNativeElement?.options[index].disabled && this._selectNativeElement?.options[index].value == '') {
			this._selectedElement.classList.add('lta-select__selected--placeholder');
		}

		this.generateItems();
	}

	generateItems() {
		const fragment = document.createDocumentFragment();
		const parentId = this._selectedElement?.getAttribute('id') || '';

		for (let index = 0; index < this._selectNativeElement?.length; index++) {
			// Create the new select items
			const option = this._selectNativeElement.options[index];
			const value = option.value;

			const selectItem = document.createElement('div');
			selectItem.textContent = option.textContent;

			// if it has a placeholder (this is an option element) hide the item
			if (index == 0 && value === '') {
				selectItem.hidden = true;
				selectItem.style.display = 'none';
			} else {
				selectItem.setAttribute('id', `${parentId}--option-${index}`);
				selectItem.setAttribute('class', 'lta-select__item');
				selectItem.setAttribute('role', 'option');
				selectItem.setAttribute('aria-selected', 'false');
				selectItem.dataset.ssId = value;

				this._items.push(selectItem);
			}

			fragment.append(selectItem);
		}

		this._itemsWrapper.append(fragment);

		const nativeSelectedOptionText =
			this._selectNativeElement.options[this._selectNativeElement.selectedIndex]?.textContent;
		if (nativeSelectedOptionText) {
			// first child is the span element
			this._selectedElement.children[0].textContent = nativeSelectedOptionText;
		}

		const selectedElement = SelectorEngine.findOne(':not([disabled])[selected]', this._selectNativeElement);
		const options = SelectorEngine.find('.lta-select__item', this._itemsWrapper);
		for (const option of options) {
			const value = Manipulator.getDataAttribute(option, 'ssId');
			if (selectedElement && selectedElement?.value === value) {
				option.classList.add(CLASSNAME_OPTION_SELECTED);
				this.state.selectedItem = option;
				this.showClearButton(true);
				// adds placeholder if it has one
				this._selectedElement.classList.remove('lta-select__selected--placeholder');
			}
		}
	}

	setEventListeners() {
		// Click on the select
		EventHandler.on(this._selectedElement, EVENT_CLICK, this.openSelect.bind(this));
		// Use of keyboard controls
		this._selectWrapper.addEventListener('keydown', this.handleKeyboardNavigation.bind(this), true);
		// Clicking on an option
		EventHandler.on(this._itemsWrapper, EVENT_MOUSEDOWN, this.handleChange.bind(this));
		// Changing focus elsewhere on the page
		EventHandler.on(this._selectWrapper, EVENT_BLUR, this.handleBlur.bind(this));

		if (this._clearButton) {
			EventHandler.on(this._clearButton, EVENT_CLEAR, this.handleClear.bind(this));
			EventHandler.on(this._clearButton, EVENT_CLEAR_KEYDOWN, (event) => {
				if (event.key === Key.Enter) {
					this.handleClear(event);
				}
			});
		}
	}
	// eslint-disable-next-line complexity
	handleKeyboardNavigation(event) {
		let activeOption = this._items.find((item) => item.classList.contains(CLASSNAME_OPTION_ACTIVE));
		if (!activeOption) {
			activeOption = this._items.find((item) => item.classList.contains(CLASSNAME_OPTION_SELECTED));
			activeOption?.classList.add(CLASSNAME_OPTION_ACTIVE);
		}

		const setOptionToActive = (option) => {
			option.classList.add(CLASSNAME_OPTION_ACTIVE);
			this._selectedElement.setAttribute('aria-activedescendant', option.id);
		};
		// eslint-disable-next-line unicorn/consistent-function-scoping
		const setOptionToInactive = (option) => {
			option.classList.remove(CLASSNAME_OPTION_ACTIVE);
		};

		const handleVerticalNavigation = (direction) => {
			if (!activeOption) {
				setOptionToActive(this._items[0]);
			} else {
				const targetOption =
					direction === OptionListNavigationDirection.Up
						? activeOption.previousElementSibling
						: activeOption.nextElementSibling;

				if (targetOption && targetOption.role === 'option') {
					setOptionToInactive(activeOption);
					setOptionToActive(targetOption);
					this._itemsWrapper.scrollTop = targetOption.offsetTop;
				}
			}
		};

		const navigationKeys = new Set([Key.Space, Key.ArrowDown, Key.ArrowUp]);
		if (!this.state.opened) {
			if (navigationKeys.has(event.key)) {
				this.openSelect(event);
			}
			return;
		}

		// Prevent page movement
		if (navigationKeys.has(event.key)) {
			event.preventDefault();
		}

		// Apply the current state
		const selectionKeys = new Set([Key.Space, Key.Enter, Key.Tab]);
		if (selectionKeys.has(event.key)) {
			this.handleChange(event);
		}

		// Move the user though the options
		if (event.key === Key.ArrowDown) {
			handleVerticalNavigation(OptionListNavigationDirection.Down);
		}
		if (event.key === Key.ArrowUp) {
			handleVerticalNavigation(OptionListNavigationDirection.Up);
		}

		// Close the listbox
		if (event.key === Key.Escape) {
			this.closeSelect();
		}
	}

	handleBlur() {
		if (this.state.opened) {
			this.closeSelect();
		}
	}

	handleChange(event) {
		if (this.state.isMultiSelect) {
			event.stopPropagation();
			event.preventDefault();
			this.handleMultipleSelectChange(event);
		} else {
			this.handleSelectChange(event);
		}
	}

	handleSelectChange(event) {
		// this will fire if user clicks on label
		if (event.target === this._selectNativeElement) {
			this._selectedElement.focus();
			return;
		}

		const targetOption = this.activeEventOption(event);

		const hasChanged = targetOption !== this.state.selectedItem;

		// remove active from previous selected item
		if (this.state.selectedItem && hasChanged) {
			this.state.selectedItem.classList.remove(CLASSNAME_OPTION_SELECTED);
			this.state.selectedItem.ariaSelected = 'false';
		}

		// active for new selected item
		if (hasChanged) {
			const updateNativeElement = () => {
				if (this._selectNativeElement) {
					const previousSelect = this._selectNativeElement?.options[this._selectNativeElement.selectedIndex];
					previousSelect.removeAttribute('selected');
					this._selectNativeElement.selectedIndex = this.state.selectedIndex;
					this._selectNativeElement.options[this.state.selectedIndex].setAttribute('selected', 'true');
					this._selectNativeElement.dispatchEvent(this.onchangeEvent);
				}
			};

			this.state.selectedItem = targetOption;
			this.state.selectedIndex = [...this._itemsWrapper.children].indexOf(targetOption);
			this.state.selectedItem.classList.add(CLASSNAME_OPTION_SELECTED);
			this.state.selectedItem.ariaSelected = 'true';

			updateNativeElement();

			this._selectedElement.children[0].textContent = this.state.selectedItem.textContent;
			if (this._selectedElement.classList.contains('lta-select__selected--placeholder')) {
				this._selectedElement.classList.remove('lta-select__selected--placeholder');
			}
		}
		this.showClearButton(true);
		this._selectedElement.focus();
		if(targetOption) {
			this.closeSelect();
		}
	}

	handleMultipleSelectChange(event) {
		if (event.key === Key.Enter) {
			this._selectedElement.focus();
			this.closeSelect();
		} else {
			const targetOption = this.activeEventOption(event);
			this.state.selectedItem = targetOption;
			this.state.selectedItem.classList.toggle(CLASSNAME_OPTION_SELECTED);
			this.selectedOptions = SelectorEngine.find(`.${CLASSNAME_OPTION_SELECTED}`, this._itemsWrapper);
			this._selectNativeElement.dispatchEvent(this.onchangeEvent);
		}
	}

	handleClear(event) {
		if (this.state.isMultiSelect) {
			for (const option of this.selectedOptions) {
				option.classList.remove(CLASSNAME_OPTION_SELECTED);
			}
			this.selectedOptions = [];
		}
		this.reset();
		event.stopPropagation();
	}

	renderMultiSelect() {
		const optionValues = this.selectedOptions.map((option) => '' + Manipulator.getDataAttribute(option, 'ssId'));
		const optionLabels = this.selectedOptions.map((option) => option.textContent);
		for (const option of this._selectNativeElement?.options) {
			option.selected = optionValues.includes(option.value);
		}
		if (optionValues.length > 0) {
			this.state.selectedItem = this._selectNativeElement?.options[this._selectNativeElement.selectedIndex];
			this._selectedElement.children[0].textContent = optionLabels.join(', ');
			this._selectedElement.classList.remove('lta-select__selected--placeholder');
			this.showClearButton(true);
		} else {
			this.reset();
		}
	}

	reset() {
		if (this.state.selectedItem) {
			this.state.selectedItem.classList.remove(CLASSNAME_OPTION_SELECTED);
			this.state.selectedItem.ariaSelected = 'false';
			this._selectNativeElement.selectedIndex = 0;
			this.state.selectedItem =  undefined;
			this.state.selectedIndex =  undefined;
			this._selectNativeElement.dispatchEvent(this.onchangeEvent);
			this._selectNativeElement.selectedIndex = 0;
			// first children is the span element
			this._selectedElement.children[0].textContent =
				this._selectNativeElement?.options[this._selectNativeElement.selectedIndex].textContent;

			// adds placeholder if it has one
			const index = this._selectNativeElement.selectedIndex;
			if (this._selectNativeElement?.options[index].disabled && this._selectNativeElement?.options[index].value == '') {
				this._selectedElement.classList.add('lta-select__selected--placeholder');
			}
			this.showClearButton(false);
		}
	}

	openSelect(event) {
		event.stopPropagation();

		this.state.opened = this.state.opened == true ? false : true;
		const ariaExpanded = this._selectedElement.ariaExpanded;
		this._selectedElement.ariaExpanded = ariaExpanded == 'true' ? 'false' : 'true';
		this._selectedElement.classList.toggle('lta-select__selected--active');
		this._itemsWrapper.classList.toggle('lta-select__items-wrapper--hide');
		this._selectWrapper.classList.toggle('lta-select__wrapper--opened');
	}

	closeSelect() {
		this.state.opened = false;
		this._selectedElement.ariaExpanded = 'false';
		this._itemsWrapper.classList.add('lta-select__items-wrapper--hide');
		this._selectedElement.classList.remove('lta-select__selected--active');
		this._selectWrapper.classList.remove('lta-select__wrapper--opened');
		this._itemsWrapper.querySelector(`.${CLASSNAME_OPTION_ACTIVE}`)?.classList.remove(CLASSNAME_OPTION_ACTIVE);
		this._selectedElement.setAttribute('aria-activedescendant', '');
	}

	clearItems() {
		const options = SelectorEngine.find('div', this._itemsWrapper);
		for (const option of options) {
			option.remove();
		}
	}

	setError(message, element) {
		if (!element) {
			const errorElement = SelectorEngine.findOne('.lta-text-input--error', this._element);
			errorElement.textContent = message;
		} else {
			element.textContent = message;
		}
	}

	showClearButton(flag) {
		if (this._clearButton) {
			this._clearButton.classList.toggle(ClassNames.Hidden, !flag);
			this._dropdownCaret.classList.toggle(ClassNames.Hidden, flag);
		}
	}

	updateViewOptions() {
		const values = [];
		for (const option of this.selectedOptions) {
			values.push(option.value);
		}
		const displayOptions = SelectorEngine.find('.lta-select__item', this._itemsWrapper);
		for (const option of displayOptions) {
			const ssId = '' + Manipulator.getDataAttribute(option, 'ssId');
			option.classList.toggle(CLASSNAME_OPTION_SELECTED, values.includes(ssId));
		}
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	// eslint-disable-next-line no-invalid-this
	Select.getInstance(this) || new Select(this);
});

export default Select;
